import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import {
  ShieldCheck,
  AlertCircle,
  PackageCheck,
  RefreshCw,
  Boxes,
  ArrowRight,
  CheckCircle2,
  FileText,
  TrendingUp,
} from "lucide-react";
import {
  getReconciliationPreview,
  applyInventoryReconciliation,
  ReconciliationPreview,
} from "@/lib/inventory/engine";

interface InventoryReconciliationDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  companyId: string;
  onReconciliationCompleted?: () => void;
}

export function InventoryReconciliationDialog({
  open,
  onOpenChange,
  companyId,
  onReconciliationCompleted,
}: InventoryReconciliationDialogProps) {
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [preview, setPreview] = useState<ReconciliationPreview | null>(null);
  const { toast } = useToast();

  const fetchPreview = async () => {
    if (!companyId) return;
    setLoading(true);
    try {
      const data = await getReconciliationPreview(companyId);
      setPreview(data);
    } catch (err: any) {
      toast({
        title: "Audit Check Failed",
        description: err.message || "Could not retrieve reconciliation preview.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open && companyId) {
      fetchPreview();
    }
  }, [open, companyId]);

  const handleApply = async () => {
    if (!companyId) return;
    setApplying(true);
    try {
      const result = await applyInventoryReconciliation(companyId);
      toast({
        title: "Reconciliation Applied Successfully",
        description: `Created ${result.itemsCreatedCount || 0} catalog item(s) and ${result.movementsCreated || 0} stock movement(s).`,
      });
      await fetchPreview();
      if (onReconciliationCompleted) {
        onReconciliationCompleted();
      }
    } catch (err: any) {
      toast({
        title: "Reconciliation Error",
        description: err.message || "Failed to apply reconciliation.",
        variant: "destructive",
      });
    } finally {
      setApplying(false);
    }
  };

  const isReconciled = preview?.isReconciled ?? false;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] flex flex-col p-6 overflow-hidden">
        <DialogHeader className="pb-3 border-b border-border/70">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2.5">
              <div className="w-9 h-9 rounded-lg bg-blue-500/10 text-blue-600 dark:text-blue-400 flex items-center justify-center border border-blue-500/20">
                <ShieldCheck className="h-5 w-5" />
              </div>
              <div>
                <DialogTitle className="text-lg font-bold font-display text-foreground">
                  Inventory Stock Audit & Safe Reconciliation
                </DialogTitle>
                <DialogDescription className="text-xs text-muted-foreground mt-0.5">
                  Verify approved purchase/sales invoices against physical stock movements and catalog records with zero duplicates.
                </DialogDescription>
              </div>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={fetchPreview}
              disabled={loading || applying}
              className="h-8 text-xs gap-1.5"
            >
              <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} />
              Scan Again
            </Button>
          </div>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto space-y-4 py-2 pr-1">
          {loading && !preview && (
            <div className="py-12 flex flex-col items-center justify-center text-center">
              <RefreshCw className="h-8 w-8 text-blue-500 animate-spin mb-3" />
              <p className="text-sm font-medium text-foreground">Auditing Vouchers & Inventory Ledger...</p>
              <p className="text-xs text-muted-foreground">Checking purchase invoices, sales vouchers, and stock movements...</p>
            </div>
          )}

          {preview && (
            <>
              {/* Status Banner */}
              {isReconciled ? (
                <div className="flex items-center gap-3 p-4 rounded-xl bg-emerald-500/10 border border-emerald-500/25 text-emerald-800 dark:text-emerald-200">
                  <CheckCircle2 className="h-5 w-5 text-emerald-600 dark:text-emerald-400 shrink-0" />
                  <div className="flex-1">
                    <p className="text-sm font-semibold">All Approved Vouchers & Stock Movements are 100% In Sync</p>
                    <p className="text-xs opacity-90 mt-0.5">
                      Every physical purchase and sale has an exact matching stock movement and accurate Weighted Average Cost (WAC). No missing entries detected.
                    </p>
                  </div>
                </div>
              ) : (
                <div className="flex items-center justify-between gap-3 p-4 rounded-xl bg-amber-500/10 border border-amber-500/25 text-amber-900 dark:text-amber-200">
                  <div className="flex items-center gap-3">
                    <AlertCircle className="h-5 w-5 text-amber-600 dark:text-amber-400 shrink-0" />
                    <div>
                      <p className="text-sm font-semibold">Stock Discrepancies Detected</p>
                      <p className="text-xs opacity-90 mt-0.5">
                        Found {preview.summary.missingMovementsCount} missing stock movement(s) or catalog records from historical vouchers that need recovery.
                      </p>
                    </div>
                  </div>
                  <Button
                    onClick={handleApply}
                    disabled={applying}
                    size="sm"
                    className="bg-blue-600 hover:bg-blue-500 text-white font-semibold text-xs gap-1.5 shrink-0"
                  >
                    <RefreshCw className={`h-3.5 w-3.5 ${applying ? "animate-spin" : ""}`} />
                    {applying ? "Applying..." : "Apply Safe Reconciliation"}
                  </Button>
                </div>
              )}

              {/* KPI Summary Cards */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <div className="p-3 rounded-lg border border-border bg-card/60">
                  <span className="text-[11px] text-muted-foreground uppercase font-medium">Approved Vouchers</span>
                  <div className="text-lg font-bold text-foreground mt-0.5">{preview.summary.totalApprovedVouchers}</div>
                </div>
                <div className="p-3 rounded-lg border border-border bg-card/60">
                  <span className="text-[11px] text-muted-foreground uppercase font-medium">Catalog Items</span>
                  <div className="text-lg font-bold text-foreground mt-0.5">{preview.summary.totalExistingCatalogueItems}</div>
                </div>
                <div className="p-3 rounded-lg border border-border bg-card/60">
                  <span className="text-[11px] text-muted-foreground uppercase font-medium">Missing Catalog</span>
                  <div className="text-lg font-bold text-foreground mt-0.5">{preview.summary.missingCatalogueItemsCount}</div>
                </div>
                <div className="p-3 rounded-lg border border-border bg-card/60">
                  <span className="text-[11px] text-muted-foreground uppercase font-medium">Missing Movements</span>
                  <div className="text-lg font-bold text-foreground mt-0.5">{preview.summary.missingMovementsCount}</div>
                </div>
              </div>

              {/* Missing Catalog Items (Genuinely new items from purchase invoices) */}
              {preview.missingCatalogueItems && preview.missingCatalogueItems.length > 0 && (
                <div className="space-y-2">
                  <div className="flex items-center gap-1.5 text-xs font-bold text-foreground">
                    <Boxes className="h-4 w-4 text-blue-500" />
                    <span>Items to Auto-Create in Catalogue ({preview.missingCatalogueItems.length})</span>
                  </div>
                  <div className="rounded-lg border border-border/80 overflow-hidden bg-card/60">
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="bg-muted/50 border-b border-border/70 text-[10.5px] uppercase tracking-wider text-muted-foreground font-semibold">
                          <th className="text-left py-2 px-3">Voucher No</th>
                          <th className="text-left py-2 px-3">Date</th>
                          <th className="text-left py-2 px-3">Supplier / Party</th>
                          <th className="text-left py-2 px-3">Item Name</th>
                          <th className="text-left py-2 px-3">Unit</th>
                          <th className="text-right py-2 px-3">Qty</th>
                          <th className="text-right py-2 px-3">Unit Rate</th>
                          <th className="text-right py-2 px-3">Amount</th>
                        </tr>
                      </thead>
                      <tbody>
                        {preview.missingCatalogueItems.map((itm: any, idx: number) => (
                          <tr key={idx} className="border-b border-border/30 last:border-b-0 hover:bg-muted/20">
                            <td className="py-2 px-3 font-semibold text-blue-600 dark:text-blue-400">{itm.voucherNumber}</td>
                            <td className="py-2 px-3 text-muted-foreground">{itm.voucherDate}</td>
                            <td className="py-2 px-3 text-foreground">{itm.partyName || "Supplier"}</td>
                            <td className="py-2 px-3 font-medium text-foreground">{itm.name}</td>
                            <td className="py-2 px-3 text-muted-foreground">{itm.unit}</td>
                            <td className="py-2 px-3 text-right font-mono font-medium">{itm.qty}</td>
                            <td className="py-2 px-3 text-right font-mono">₹{Number(itm.rate).toLocaleString("en-IN")}</td>
                            <td className="py-2 px-3 text-right font-mono font-bold">₹{Number(itm.amount).toLocaleString("en-IN")}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Missing Stock Movements */}
              {preview.missingMovements && preview.missingMovements.length > 0 && (
                <div className="space-y-2">
                  <div className="flex items-center gap-1.5 text-xs font-bold text-foreground">
                    <FileText className="h-4 w-4 text-amber-500" />
                    <span>Missing Stock Movements to Recover ({preview.missingMovements.length})</span>
                  </div>
                  <div className="rounded-lg border border-border/80 overflow-hidden bg-card/60">
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="bg-muted/50 border-b border-border/70 text-[10.5px] uppercase tracking-wider text-muted-foreground font-semibold">
                          <th className="text-left py-2 px-3">Voucher No</th>
                          <th className="text-left py-2 px-3">Date</th>
                          <th className="text-left py-2 px-3">Product Name</th>
                          <th className="text-left py-2 px-3">Direction</th>
                          <th className="text-right py-2 px-3">Quantity</th>
                          <th className="text-right py-2 px-3">Cost Rate</th>
                          <th className="text-right py-2 px-3">Total Value</th>
                          <th className="text-left py-2 px-3">Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {preview.missingMovements.map((mov: any, idx: number) => (
                          <tr key={idx} className="border-b border-border/30 last:border-b-0 hover:bg-muted/20">
                            <td className="py-2 px-3 font-semibold text-blue-600 dark:text-blue-400">{mov.voucherNumber}</td>
                            <td className="py-2 px-3 text-muted-foreground">{mov.voucherDate}</td>
                            <td className="py-2 px-3 font-medium text-foreground">{mov.itemName}</td>
                            <td className="py-2 px-3">
                              <Badge variant={mov.type === "In" ? "default" : "destructive"} className="text-[10px] font-semibold">
                                {mov.type === "In" ? "+ Stock In" : "- Stock Out"}
                              </Badge>
                            </td>
                            <td className="py-2 px-3 text-right font-mono font-medium">{mov.quantity}</td>
                            <td className="py-2 px-3 text-right font-mono">₹{Number(mov.rate).toLocaleString("en-IN")}</td>
                            <td className="py-2 px-3 text-right font-mono font-bold">₹{Number(mov.value).toLocaleString("en-IN")}</td>
                            <td className="py-2 px-3">
                              <span className="text-[10.5px] font-medium text-emerald-600 dark:text-emerald-400">
                                Will Create Stock Movement
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Projected Stock Changes */}
              {preview.projectedStockChanges && preview.projectedStockChanges.length > 0 && (
                <div className="space-y-2">
                  <div className="flex items-center gap-1.5 text-xs font-bold text-foreground">
                    <TrendingUp className="h-4 w-4 text-emerald-500" />
                    <span>Projected Stock Impact After Safe Reconciliation</span>
                  </div>
                  <div className="rounded-lg border border-border/80 overflow-hidden bg-card/60">
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="bg-muted/50 border-b border-border/70 text-[10.5px] uppercase tracking-wider text-muted-foreground font-semibold">
                          <th className="text-left py-2 px-3">Product Name</th>
                          <th className="text-right py-2 px-3">Current Stock</th>
                          <th className="text-center py-2 px-3"></th>
                          <th className="text-right py-2 px-3">Projected Stock</th>
                          <th className="text-left py-2 px-3">Catalogue Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {preview.projectedStockChanges.map((proj: any, idx: number) => (
                          <tr key={idx} className="border-b border-border/30 last:border-b-0 hover:bg-muted/20">
                            <td className="py-2 px-3 font-semibold text-foreground">{proj.name}</td>
                            <td className="py-2 px-3 text-right font-mono text-muted-foreground">{proj.currentQty} Nos</td>
                            <td className="py-2 px-3 text-center">
                              <ArrowRight className="h-3.5 w-3.5 mx-auto text-blue-500" />
                            </td>
                            <td className="py-2 px-3 text-right font-mono font-bold text-emerald-600 dark:text-emerald-400">
                              {proj.projectedQty} Nos
                            </td>
                            <td className="py-2 px-3">
                              {proj.isNewItem ? (
                                <Badge variant="outline" className="text-[10px] bg-purple-500/10 text-purple-600 border-purple-500/20">
                                  New Item to be Created
                                </Badge>
                              ) : (
                                <Badge variant="outline" className="text-[10px] bg-blue-500/10 text-blue-600 border-blue-500/20">
                                  Existing Item Updated
                                </Badge>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        <div className="pt-3 border-t border-border flex items-center justify-between">
          <span className="text-[11px] text-muted-foreground font-mono">
            AccouPro Inventory Engine · Weighted Average Costing (WAC)
          </span>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => onOpenChange(false)} className="text-xs">
              Close
            </Button>
            {!isReconciled && (
              <Button
                onClick={handleApply}
                disabled={applying}
                size="sm"
                className="bg-primary text-primary-foreground font-medium text-xs gap-1.5"
              >
                <PackageCheck className="h-3.5 w-3.5" />
                {applying ? "Applying..." : "Apply Reconciliation"}
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
